import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../components/button";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onPageChange,
}) => {
  const { t } = useTranslation();

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show max 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // If total pages is small, show all pages
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(0);

      if (currentPage <= 3) {
        // Show first 5 pages + ellipsis + last page
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        // Show first page + ellipsis + last 5 pages
        pages.push("ellipsis");
        for (let i = totalPages - 5; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page + ellipsis + current page ± 1 + ellipsis + last page
        pages.push("ellipsis");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("ellipsis");
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  const pagination = useMemo(
    () => (
      <div className="bg-white px-6 py-3 flex items-center justify-between gap-3 border-t border-[#E9EAEB]">
        {/* Previous Button */}
        <Button
          variant="outline"
          onClick={onPrevPage}
          disabled={currentPage === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white text-[#535862] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("previous")}
        </Button>

        {/* Page Numbers */}
        <div className="flex gap-3">
          {getPageNumbers().map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="w-10 h-10 flex items-center justify-center text-[#535862]"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange?.(page as number)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${
                  currentPage === page
                    ? "bg-[#FAFAFA] text-[#535862] "
                    : "text-[#535862] hover:bg-gray-50"
                }`}
              >
                {(page as number) + 1}
              </button>
            )
          )}
        </div>

        {/* Next Button */}
        <Button
          variant="outline"
          onClick={onNextPage}
          disabled={currentPage === totalPages - 1}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white text-[#535862] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("next")}
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Button>
      </div>
    ),
    [totalPages, currentPage, onPrevPage, onNextPage, onPageChange, t]
  );

  // Guard against invalid totalPages
  if (!totalPages || totalPages <= 0 || isNaN(totalPages)) {
    return null;
  }

  return pagination;
};

export default Pagination;
